import axiosRaw from 'axios';
import axios from '../axiosInstance';
import {
  Website,
  WebsiteSummary,
  WebsiteTestResult,
  WebsiteHistory,
  CreateWebsiteRequest,
  UpdateWebsiteRequest,
  WebsiteFilters,
} from '../../containers/pages/CANsense/types/website';
import { getApiBaseUrl, getForwardedHost } from './apiClient';

/**
 * CANsense Website Monitoring API Client
 * Handles all communication with the website monitoring backend (Blackbox Exporter)
 */

const getApiUrl = (endpoint: string): string => {
  return `${getApiBaseUrl()}/sense/websites${endpoint}`;
};

export class WebsiteService {
  async getWebsites(filters?: WebsiteFilters): Promise<{ websites: Website[]; summary: WebsiteSummary }> {
    const API_URL = getApiUrl('');
    const response = await axios.get(API_URL, {
      params: filters,
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });
    const unwrapped = response.data?.data || response.data;
    return {
      websites: unwrapped?.websites ?? [],
      summary: unwrapped?.summary ?? {
        total: 0,
        up: 0,
        down: 0,
        paused: 0,
        avg_response_time_ms: null,
        ssl_expiring_soon: 0,
      },
    };
  }

  async getWebsite(id: number): Promise<Website> {
    const API_URL = getApiUrl(`/${id}`);
    const response = await axios.get(API_URL, {
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });
    return response.data?.data || response.data;
  }

  async testUrl(url: string, probeType: string = 'http_2xx', port?: number, timeout: string = '10s'): Promise<WebsiteTestResult> {
    const API_URL = getApiUrl('/test');

    // For ssl_cert probes with a custom port, send as host:port format
    let testTarget = url;
    if (probeType === 'ssl_cert' && port && port !== 443) {
      try {
        const parsedUrl = new URL(url);
        testTarget = `${parsedUrl.hostname}:${port}`;
      } catch {
        // If URL parsing fails, pass as-is
      }
    }

    try {
      const response = await axios.post(
        API_URL,
        { url: testTarget, probe_type: probeType, timeout },
        {
          headers: {
            'X-Forwarded-Host': getForwardedHost(API_URL),
          },
          timeout: 30000,
        }
      );
      return response.data?.data || response.data;
    } catch (error) {
      if (axiosRaw.isAxiosError(error) && error.response?.data) {
        const errData = error.response.data?.data || error.response.data;
        return errData as WebsiteTestResult;
      }
      return {
        success: false,
        reachable: false,
        error: error instanceof Error ? error.message : 'URL test failed',
      };
    }
  }

  async createWebsite(websiteData: CreateWebsiteRequest): Promise<{ success: boolean; website: Website; test_result?: WebsiteTestResult }> {
    const API_URL = getApiUrl('');
    const response = await axios.post(API_URL, websiteData, {
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });
    return response.data?.data || response.data;
  }

  async updateWebsite(id: number, websiteData: UpdateWebsiteRequest): Promise<Website> {
    const API_URL = getApiUrl(`/${id}`);
    const response = await axios.put(API_URL, websiteData, {
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });
    return response.data?.data || response.data;
  }

  async deleteWebsite(id: number): Promise<void> {
    const API_URL = getApiUrl(`/${id}`);
    await axios.delete(API_URL, {
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });
  }

  async getWebsiteHistory(id: number, period: '1h' | '24h' | '7d' | '30d' = '24h'): Promise<WebsiteHistory> {
    const API_URL = getApiUrl(`/${id}/history`);
    const response = await axios.get(API_URL, {
      params: { period },
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });
    return response.data?.data || response.data;
  }

  async pauseWebsite(id: number): Promise<Website> {
    const API_URL = getApiUrl(`/${id}/pause`);
    const response = await axios.post(API_URL, {}, {
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });
    return response.data?.data || response.data;
  }

  async resumeWebsite(id: number): Promise<Website> {
    const API_URL = getApiUrl(`/${id}/resume`);
    const response = await axios.post(API_URL, {}, {
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });
    return response.data?.data || response.data;
  }
}

// Export singleton instance
export const websiteService = new WebsiteService();

// Export with alternative name for convenience
export const websiteApi = websiteService;
